import type { SortingFn } from "@tanstack/react-table";
import type { DefaultTheme } from "styled-components";
import type { JobMetadata } from "@/types/api";

const STATUS_COLORS: Record<string, (t: DefaultTheme) => string> = {
  running: (t) => t.badgeConfirmSubtleColor,
  stopped: (t) => t.badgeNeutralSubtleColor,
  failed: (t) => t.badgeErrorSubtleColor,
  starting: (t) => t.badgeWarningSubtleColor,
  stopping: (t) => t.badgeWarningSubtleColor,
};

export function getStatusColor(state: string, theme: DefaultTheme): string {
  const lookup = STATUS_COLORS[state.toLowerCase()];
  return lookup ? lookup(theme) : theme.badgeNeutralSubtleColor;
}

/** The capitalized subset of JobState that normalizeState produces. */
export type NormalizedJobState =
  | "Running"
  | "Stopped"
  | "Failed"
  | "Starting"
  | "Stopping";

const STATE_MAP: Record<string, NormalizedJobState> = {
  running: "Running",
  stopped: "Stopped",
  failed: "Failed",
  starting: "Starting",
  stopping: "Stopping",
};

export function normalizeState(state: string): NormalizedJobState {
  return STATE_MAP[state.toLowerCase()] ?? "Stopped";
}

export interface FormattedPipeline {
  source: string;
  middlewares: string[];
  /** First sink (or "—") — for single-sink consumers like Dashboard. */
  sink: string;
  sinks: string[];
}

export function formatPipeline(
  job: Pick<JobMetadata, "pipeline">,
): FormattedPipeline {
  const pipeline = job.pipeline;
  if (!pipeline) return { source: "—", middlewares: [], sink: "—", sinks: [] };

  const source = `${pipeline.source.service_name}.${pipeline.source.resource}`;
  const middlewares = pipeline.middlewares?.map((m) => m.resource) ?? [];
  const sinks = pipeline.sinks.map((s) => `${s.service_name}.${s.resource}`);

  return { source, middlewares, sink: sinks[0] ?? "—", sinks };
}

const TIME_UNITS: [number, string][] = [
  [86400, "d"],
  [3600, "h"],
  [60, "m"],
];

export function formatTimeAgo(dateString: string): string {
  const diffSecs = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000,
  );

  for (const [threshold, unit] of TIME_UNITS) {
    const value = Math.floor(diffSecs / threshold);
    if (value > 0) return `${value}${unit} ago`;
  }

  return "just now";
}

// Status sort priority, ordered along the lifecycle from the "up" states toward
// the "down" states: Running → Starting → Stopped → Stopping → Failed. Rank 0
// is Running, so ascending (the default, down-arrow click) surfaces running
// pipelines at the top and the up arrow flips to failed-first. Alphabetical (the
// default) has no operational meaning. Shared by the Pipelines list and the
// dashboard's Recent pipelines card.
const STATUS_SORT_ORDER: Record<NormalizedJobState, number> = {
  Running: 0,
  Starting: 1,
  Stopped: 2,
  Stopping: 3,
  Failed: 4,
};

export const sortByStatusPriority: SortingFn<JobMetadata> = (
  rowA,
  rowB,
  columnId,
) => {
  const a = STATUS_SORT_ORDER[rowA.getValue<NormalizedJobState>(columnId)];
  const b = STATUS_SORT_ORDER[rowB.getValue<NormalizedJobState>(columnId)];
  return a - b;
};
